/**
 * Analytics utility for tracking user events via Google Tag Manager dataLayer
 * Provides safe event tracking with error handling to prevent application crashes
 */

/**
 * Interface for dataLayer events
 */
interface DataLayerEvent {
  event: string
  [key: string]: string | number | boolean | undefined
}

/**
 * Interface for signup-related event data
 */
interface SignupEventData {
  step?: string
  account_type?: 'personal' | 'business' | 'league'
  user_type?: string
  email_hash?: string
  company_name?: string
  context?: string
  context_id?: string
  from_step?: number
  to_step?: number
}

/**
 * Wizard tracking types
 */
type WizardName = 'onboarding' | 'competition'
type WizardAction =
  | 'start'
  | 'skip'
  | 'close'
  | 'continue'
  | 'finish'
  // step-specific "verbs"
  | 'league_layout'
  | 'sports_setup'
  | 'first_competition'
  | 'structure'
  | 'division'
  | 'conference'

/**
 * Interface for wizard-related event data
 */
interface WizardEventData {
  step?: string // e.g., 'get_started', 'league_layout', 'structure'
  choice?: string // e.g., 'simple_league' | 'specialist' | 'single_division' | ...
  sport_count?: number
  competition_name?: string
  sport?: string
  subleague?: string
  division_name?: string
  conference_count?: number
  from_step?: number
  to_step?: number
  context?: string
  context_id?: string
  user_type?: string
  account_type?: string
  email_hash?: string
}

/**
 * Creates a simple hash from a string (for privacy-safe email tracking)
 * @param str - String to hash
 * @returns Simple hash of the input string
 */
const createSimpleHash = (str: string): string => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36)
}

/**
 * Safely pushes an event to the dataLayer
 * Includes error handling to prevent crashes if dataLayer is unavailable
 *
 * @param eventName - Name of the event to track
 * @param eventData - Additional data to include with the event
 */
export const trackEvent = (
  eventName: string,
  eventData: SignupEventData | WizardEventData = {}
): void => {
  try {
    // Ensure dataLayer exists
    if (typeof window === 'undefined') {
      console.warn(
        '🔄 trackEvent: dataLayer not available, event not tracked:',
        eventName
      )
      return
    }

    // Initialize dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || []

    // Prepare the event object
    const event: DataLayerEvent = {
      event: eventName,
      timestamp: Date.now(),
      ...eventData,
    }

    // Push to dataLayer
    window.dataLayer.push(event)
  } catch (error) {
    // Log error but don't throw to prevent application crashes
    console.error('🚨 trackEvent: Failed to track event:', eventName, error)
  }
}

/**
 * Tracks signup step selection events
 *
 * @param accountType - Type of account selected ('personal' or 'business')
 */
export const trackSignupSelection = (
  accountType: 'personal' | 'business'
): void => {
  trackEvent('signup_step1_selection', {
    account_type: accountType,
    step: 'selection',
  })
}

/**
 * Tracks signup step progression
 *
 * @param fromStep - Current step number
 * @param toStep - Target step number
 * @param accountType - Type of account being created
 */
export const trackSignupStepProgression = (
  fromStep: number,
  toStep: number,
  accountType?: 'personal' | 'business'
): void => {
  trackEvent('signup_step_progression', {
    from_step: fromStep,
    to_step: toStep,
    account_type: accountType,
  })
}

/**
 * Tracks signup form submission for personal accounts
 *
 * @param email - User email (will be hashed for privacy)
 * @param userType - Type of user (player, admin, team_rep)
 * @param context - Signup context
 * @param contextId - Context ID
 */
export const trackPersonalSignupSubmit = (
  email: string,
  userType: string,
  context: string,
  contextId: string
): void => {
  trackEvent('signup_submit', {
    account_type: 'personal',
    user_type: userType,
    email_hash: createSimpleHash(email),
    context,
    context_id: contextId,
    step: 'personal_complete',
  })
}

/**
 * Tracks business signup step 1 completion (moving from personal info to business info)
 *
 * @param email - User email (will be hashed for privacy)
 * @param context - Signup context
 * @param contextId - Context ID
 */
export const trackBusinessSignupStep1 = (
  email: string,
  context: string,
  contextId: string
): void => {
  trackEvent('signup_step1', {
    account_type: 'business',
    email_hash: createSimpleHash(email),
    context,
    context_id: contextId,
    step: 'business_personal_info_complete',
  })
}

/**
 * Tracks business signup final submission
 *
 * @param email - User email (will be hashed for privacy)
 * @param companyName - Company/organization name
 * @param context - Signup context
 * @param contextId - Context ID
 */
export const trackBusinessSignupSubmit = (
  email: string,
  companyName: string,
  context: string,
  contextId: string
): void => {
  trackEvent('signup_submit', {
    account_type: 'business',
    company_name: companyName,
    email_hash: createSimpleHash(email),
    context,
    context_id: contextId,
    step: 'business_complete',
  })
}

/**
 * Creates wizard event name from action and wizard type
 * @param action - The wizard action to track
 * @param wizard - The wizard type (onboarding or competition)
 * @returns Formatted event name
 */
const buildWizardEventName = (
  action: WizardAction,
  wizard: WizardName
): string => `${action}_${wizard}_wizard`

/**
 * Generic wizard event tracking helper
 * @param wizard - The wizard type
 * @param action - The action being performed
 * @param data - Additional event data
 */
export const trackWizardEvent = (
  wizard: WizardName,
  action: WizardAction,
  data: WizardEventData = {}
): void => {
  const eventName = buildWizardEventName(action, wizard)
  // Set a default context so GTM/GA4 know it's a wizard event
  const base = { context: `${wizard}_wizard` }
  trackEvent(eventName, { ...base, ...data })
}

/**
 * ========== Onboarding Wizard Tracking ==========
 */

/**
 * Tracks onboarding wizard start event
 */
export const trackOnboardingStart = (): void =>
  trackWizardEvent('onboarding', 'start', { step: 'get_started' })

/**
 * Tracks onboarding league layout choice
 * @param choice - The layout choice selected
 */
export const trackOnboardingLeagueLayout = (
  choice: 'simple_league' | 'specialist'
): void =>
  trackWizardEvent('onboarding', 'league_layout', {
    step: 'league_layout',
    choice,
  })

/**
 * Tracks onboarding sports setup completion
 * @param sportCount - Number of sports selected
 */
export const trackOnboardingSportsSetup = (sportCount: number): void =>
  trackWizardEvent('onboarding', 'sports_setup', {
    step: 'sports_setup',
    sport_count: sportCount,
  })

/**
 * Tracks onboarding first competition creation
 * @param opts - Competition details
 */
export const trackOnboardingFirstCompetition = (opts: {
  competition_name: string
  sport: string
  subleague: string
}): void =>
  trackWizardEvent('onboarding', 'first_competition', {
    step: 'first_competition',
    ...opts,
  })

/**
 * Tracks onboarding wizard completion
 */
export const trackOnboardingFinish = (): void =>
  trackWizardEvent('onboarding', 'finish', { step: 'handoff_to_competition' })

/**
 * Tracks onboarding wizard skip event
 * @param currentStep - The step being skipped from
 */
export const trackOnboardingSkip = (currentStep: string): void =>
  trackWizardEvent('onboarding', 'skip', { step: currentStep })

/**
 * Tracks onboarding wizard close event
 * @param currentStep - The step being closed from
 */
export const trackOnboardingClose = (currentStep: string): void =>
  trackWizardEvent('onboarding', 'close', { step: currentStep })

/**
 * Tracks onboarding wizard continue/resume event
 * @param restoredStep - The step being restored to
 */
export const trackOnboardingContinue = (restoredStep: string): void =>
  trackWizardEvent('onboarding', 'continue', { step: restoredStep })

/**
 * ========== Competition Wizard Tracking ==========
 */

/**
 * Tracks competition wizard start event
 */
export const trackCompetitionStart = (): void =>
  trackWizardEvent('competition', 'start', { step: 'structure' })

/**
 * Tracks competition structure choice
 * @param choice - The structure choice selected
 */
export const trackCompetitionStructure = (
  choice: 'single_division' | 'multi_division' | 'conference_divisions'
): void =>
  trackWizardEvent('competition', 'structure', { step: 'structure', choice })

/**
 * Tracks competition division setup
 * @param divisionName - Name of the division being set up
 */
export const trackCompetitionDivision = (divisionName: string): void =>
  trackWizardEvent('competition', 'division', {
    step: 'division',
    division_name: divisionName,
  })

/**
 * Tracks competition conference setup
 * @param count - Number of conferences being set up
 */
export const trackCompetitionConference = (count: number): void =>
  trackWizardEvent('competition', 'conference', {
    step: 'conference',
    conference_count: count,
  })

/**
 * Tracks competition wizard completion
 * @param choice - The completion choice (registration or manual)
 */
export const trackCompetitionFinish = (
  choice: 'registration' | 'manual'
): void => trackWizardEvent('competition', 'finish', { step: 'finish', choice })

/**
 * Tracks competition wizard skip event
 * @param currentStep - The step being skipped from
 */
export const trackCompetitionSkip = (currentStep: string): void =>
  trackWizardEvent('competition', 'skip', { step: currentStep })

/**
 * Tracks competition wizard close event
 * @param currentStep - The step being closed from
 */
export const trackCompetitionClose = (currentStep: string): void =>
  trackWizardEvent('competition', 'close', { step: currentStep })

/**
 * Tracks competition wizard continue/resume event
 * @param restoredStep - The step being restored to
 */
export const trackCompetitionContinue = (restoredStep: string): void =>
  trackWizardEvent('competition', 'continue', { step: restoredStep })

// Extend the Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer: Array<DataLayerEvent>
  }
}
